import React from "react";
import { Box, Stack, Skeleton, Typography } from "@mui/material";
import { CardWithImage, InternatlServerError, LoadingSection, NotFoundSection } from "../../components";
import { StyledDataGrid } from "../workbook/actions/actionTable";
import { GridCellParams, GridEventListener, GridRenderCellParams, MuiEvent } from "@mui/x-data-grid-premium";

export const NounVariantListing: React.FC<{
    variants: any,
    onImageClick: any,
    selectedImage: any,
    loading?: boolean,
    error?: boolean,
    onRetry?: any,
    height?: string,
    isGrid?: boolean
}> = ({ variants, onImageClick, selectedImage, loading = false, error = false, onRetry = () => false, height = "calc(100% - 90px)", isGrid = true }) => {

    if (isGrid) {
        return (
            <Box
                sx={{
                    mt: 1,
                    height: height,
                    width: '100%',
                    overflowY: "scroll",
                    overflowX: "hidden"
                }}>
                <Stack
                    direction={"row"}
                    sx={{
                        flexWrap: "wrap"
                    }}
                >
                    {loading && Array.from(Array(6).keys()).map((arr, key) => {
                        return (
                            <Box
                                key={key}
                                sx={{
                                    height: "200px",
                                    mb: 1,
                                    mr: 1,
                                    width: { xs: "100%", sm: "200px" }
                                }}>
                                <Skeleton variant="rectangular" width={"100%"} height={200} />
                            </Box>
                        );
                    })}

                    {!loading && error && <InternatlServerError message={'Opps! Something Went Wrong, Unable to fetch Noun Variants'} onRetry={onRetry} />}
                    {!loading && !error && variants?.length === 0 && <NotFoundSection message='You are yet to map variants to this noun.' />}
                    {!loading && variants?.map((variant: any, key: number) => {
                        return <CardWithImage
                            id={`noun-variant-${key}`}
                            key={key}
                            data={variant}
                            onImageClick={onImageClick}
                            image={variant.attachments?.[0]?.url}
                            title={variant?.nounvariant_name ?? "Variant " + key}
                            isSelected={variant._id === selectedImage?._id}
                        />
                    })}
                </Stack>
            </Box>
        );
    } else {
        let Columns = [
            {
                field: "attachments",
                headerName: "Noun Variant Image",
                width: 180,
                renderCell: (params: GridRenderCellParams) => (
                    <Box>
                        <img
                            height={"50px"}
                            width={"50px"}
                            style={{ objectFit: "contain" }}
                            src={
                                params?.row?.attachments
                                    ? params?.row?.attachments?.at(-1)?.url
                                    : `/imageNotFound.jpg`
                            }
                            alt={params?.row?.attachments?.at(-1)?.file_name ?? ""}
                        />
                    </Box>
                ),
                disableColumnMenu: true,
                sortable: false,
            },
            {
                field: "nounvariant_name",
                headerName: "Noun Variant Name",
                width: 250,
                renderCell: (params: GridRenderCellParams) => {
                    if (params.rowNode.type === "group") {
                        return params.value;
                    }
                    return <Typography variant="body2">{params.row?.nounvariant_name}</Typography>;
                },
            },
            {
                field: "noun_name",
                headerName: "Noun Name",
                width: 200,
                renderCell: (params: GridRenderCellParams) => {
                    if (params.rowNode.type === "group") {
                        return params.value;
                    }
                    return <Typography variant="body2">{params.row?.noun_name}</Typography>;
                },
            },
        ];

        const handleRowClick: GridEventListener<'rowClick'> = (params) => {
            onImageClick(params.row)
        };

        return <>
            <Stack
                sx={{
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                {loading && <LoadingSection message={'Fetching Noun Variants...'} />}
                {!loading && error && <InternatlServerError message={'Opps! Something Went Wrong, Unable to fetch Noun Variants'} onRetry={onRetry} />}
                {!loading && !error && variants?.length === 0 && <NotFoundSection message='You are yet to map variants to this noun.' />}
            </Stack>
            {!loading && variants?.length > 0 &&
                <StyledDataGrid
                    id="noun-variant-dg-list-view"
                    sx={{
                        '.MuiDataGrid-row': {
                            cursor: "pointer"
                        },
                        '.MuiDataGrid-cell:focus': {
                            outlineColor: "transparent !important"
                        },
                        '.MuiDataGrid-virtualScroller': {
                            overflow: 'auto !important',
                        },
                        '.MuiDataGrid-columnHeaders': {
                            position: 'sticky',
                            top: 0,
                            zIndex: 100,
                            backgroundColor: 'white',
                        },
                        mb: 1
                    }}
                    rows={variants}
                    columns={Columns}
                    loading={loading}
                    getRowId={(row) => row._id}
                    onRowClick={handleRowClick}
                    onCellClick={(params: GridCellParams, event: MuiEvent<React.MouseEvent>) => {
                        event.defaultMuiPrevented = true;
                    }}
                    pagination={false}
                />
            }
        </>;
    }
}